import Fuse from 'fuse.js';
import type { Command, CommandPaletteContext, CommandSearchResult, RecentCommand } from './types';

export class CommandRegistry {
  private commands = new Map<string, Command>();
  private shortcuts = new Map<string, string>();
  private categories = new Map<string, Command[]>();
  private recentCommands: RecentCommand[] = [];
  private fuse: Fuse<Command>;

  constructor() {
    // Initialize Fuse.js for fuzzy search
    this.fuse = new Fuse([], {
      keys: [
        { name: 'title', weight: 0.4 },
        { name: 'subtitle', weight: 0.3 },
        { name: 'searchTerms', weight: 0.2 },
        { name: 'category', weight: 0.1 }
      ],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true
    });
  }

  /**
   * Register a new command in the registry
   */
  register(command: Command): void {
    this.commands.set(command.id, command);
    
    // Register keyboard shortcut
    if (command.shortcut) {
      const shortcutKey = this.normalizeShortcut(command.shortcut);
      this.shortcuts.set(shortcutKey, command.id);
    }

    // Add to category
    if (!this.categories.has(command.category)) {
      this.categories.set(command.category, []);
    }
    this.categories.get(command.category)!.push(command);

    // Update Fuse index
    this.updateFuseIndex();
  }

  /**
   * Register multiple commands at once
   */
  registerBatch(commands: Command[]): void {
    commands.forEach(command => this.register(command));
  }

  /**
   * Unregister a command
   */
  unregister(commandId: string): void {
    const command = this.commands.get(commandId);
    if (!command) return;

    this.commands.delete(commandId);

    // Remove shortcut
    if (command.shortcut) {
      const shortcutKey = this.normalizeShortcut(command.shortcut);
      this.shortcuts.delete(shortcutKey);
    }

    // Remove from category
    const categoryCommands = this.categories.get(command.category);
    if (categoryCommands) {
      const index = categoryCommands.findIndex(cmd => cmd.id === commandId);
      if (index >= 0) {
        categoryCommands.splice(index, 1);
      }
    }

    this.updateFuseIndex();
  }

  /**
   * Get all available commands for a given context
   */
  getCommands(context: CommandPaletteContext): Command[] {
    return Array.from(this.commands.values())
      .filter(cmd => !cmd.condition || cmd.condition())
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  /**
   * Search commands with fuzzy matching
   */
  searchCommands(query: string, context: CommandPaletteContext): CommandSearchResult[] {
    if (!query.trim()) {
      return this.getDefaultCommands(context);
    }

    const availableCommands = this.getCommands(context);
    this.fuse.setCollection(availableCommands);

    const results = this.fuse.search(query) as Array<{
      item: Command;
      score?: number;
      matches?: Array<{ indices: number[][]; value: string; key: string }>;
    }>;

    return results.map(result => ({
      item: result.item,
      score: result.score || 0,
      matches: result.matches || []
    }));
  }

  /**
   * Get default commands (recent + high priority)
   */
  private getDefaultCommands(context: CommandPaletteContext): CommandSearchResult[] {
    const availableCommands = this.getCommands(context);
    
    // Get recent commands
    const recentCommandIds = this.recentCommands
      .sort((a, b) => {
        // Sort by frequency and recency
        const frequencyDiff = b.frequency - a.frequency;
        if (frequencyDiff !== 0) return frequencyDiff;
        return b.timestamp - a.timestamp;
      })
      .slice(0, 5)
      .map(rc => rc.commandId);

    const recentCommands = recentCommandIds
      .map(id => availableCommands.find(cmd => cmd.id === id))
      .filter((cmd): cmd is Command => cmd !== undefined);

    // Get top priority commands not in recent
    const topCommands = availableCommands
      .filter(cmd => !recentCommandIds.includes(cmd.id))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0))
      .slice(0, 8);

    return [
      ...recentCommands.map(cmd => ({ item: cmd, score: 0, matches: [] })),
      ...topCommands.map(cmd => ({ item: cmd, score: 0, matches: [] }))
    ];
  }

  /**
   * Execute a keyboard shortcut
   */
  executeShortcut(keys: string[], context: CommandPaletteContext): boolean {
    const shortcutKey = this.normalizeShortcut(keys);
    const commandId = this.shortcuts.get(shortcutKey);
    
    if (commandId) {
      const command = this.commands.get(commandId);
      if (command && (!command.condition || command.condition())) {
        this.executeCommand(command);
        return true;
      }
    }
    return false;
  }

  /**
   * Execute a command and track usage
   */
  executeCommand(command: Command): void {
    try {
      command.action();
      this.trackCommandUsage(command.id);
    } catch (error) {
      console.error('Error executing command:', command.id, error);
    }
  }

  /**
   * Track command usage for analytics and recent commands
   */
  private trackCommandUsage(commandId: string): void {
    const existingIndex = this.recentCommands.findIndex(rc => rc.commandId === commandId);
    
    if (existingIndex >= 0) {
      // Update existing entry
      this.recentCommands[existingIndex].frequency += 1;
      this.recentCommands[existingIndex].timestamp = Date.now();
    } else {
      // Add new entry
      this.recentCommands.push({
        commandId,
        timestamp: Date.now(),
        frequency: 1
      });
    }

    // Keep only last 50 recent commands
    this.recentCommands = this.recentCommands
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 50);
  }

  /**
   * Get commands by category
   */
  getCommandsByCategory(category: string): Command[] {
    return this.categories.get(category) || [];
  }

  /**
   * Get all available categories
   */
  getCategories(): string[] {
    return Array.from(this.categories.keys());
  }

  /**
   * Clear all commands
   */
  clear(): void {
    this.commands.clear();
    this.shortcuts.clear();
    this.categories.clear();
    this.recentCommands = [];
    this.updateFuseIndex();
  }

  /**
   * Normalize keyboard shortcut for consistent storage
   */
  private normalizeShortcut(keys: string[]): string {
    return keys
      .map(key => key.toLowerCase())
      .sort()
      .join('+');
  }

  /**
   * Update Fuse.js search index
   */
  private updateFuseIndex(): void {
    this.fuse.setCollection(Array.from(this.commands.values()));
  }

  /**
   * Get recent commands
   */
  getRecentCommands(): RecentCommand[] {
    return [...this.recentCommands];
  }

  /**
   * Clear recent commands
   */
  clearRecentCommands(): void {
    this.recentCommands = [];
  }
}

// Global command registry instance
export const globalCommandRegistry = new CommandRegistry(); 